import React, { useMemo } from 'react'
import { useUserTimezone } from '../../hooks/useUserTimezone'
import { generateTimeSlots, isSlotBookable, type TimeSlot } from './timeSlots'
import { isFramed } from '../../../utils/isFramed'
import './TimeSlotPicker.scss'

interface TimeSlotPickerProps {
  selectedDate: Date | null
  selectedTime: string | null
  onSelectTime: (time: string) => void
}

interface TimeSlotButtonProps {
  slot: TimeSlot
  isSelected: boolean
  onSelect: (time: string) => void
}

const TimeSlotButton = ({ slot, isSelected, onSelect }: TimeSlotButtonProps) => (
  <button
    className={`time_slot_button ${isSelected ? 'time_slot_button_selected' : ''} ${!slot.available ? 'time_slot_button_unavailable' : ''}`}
    onClick={() => slot.available && onSelect(slot.time)}
    disabled={!slot.available}
  >
    {slot.time}
  </button>
)

interface TimeSlotSectionProps {
  title: string
  slots: TimeSlot[]
  selectedTime: string | null
  onSelectTime: (time: string) => void
}

const TimeSlotSection = ({ title, slots, selectedTime, onSelectTime }: TimeSlotSectionProps) => {
  if (slots.length === 0) {
    return null
  }

  return (
    <div className="time_slot_section">
      <h4 className="time_slot_section_title">{title}</h4>
      <div className="time_slot_list">
        {slots.map((slot) => (
          <TimeSlotButton
            key={slot.time}
            slot={slot}
            isSelected={selectedTime === slot.time}
            onSelect={onSelectTime}
          />
        ))}
      </div>
    </div>
  )
}

function TimeSlotPicker ({ selectedDate, selectedTime, onSelectTime }: TimeSlotPickerProps) {
  const allTimeSlots = useMemo(() => generateTimeSlots(), [])
  const { abbreviation: userTimezone } = useUserTimezone()

  // Keep only slots that start at least the booking lead time from now. Future
  // days keep every slot; the current day drops slots that are too soon (or, late
  // in the evening, all of them).
  const filteredSlots = useMemo(() => {
    if (!selectedDate) {
      return { morning: [], afternoon: [], lateAfternoon: [] }
    }

    const now = new Date()
    const filterAvailableTimes = (slots: TimeSlot[]) =>
      slots.filter((slot) => isSlotBookable(selectedDate, slot, now))

    return {
      morning: filterAvailableTimes(allTimeSlots.morning),
      afternoon: filterAvailableTimes(allTimeSlots.afternoon),
      lateAfternoon: filterAvailableTimes(allTimeSlots.lateAfternoon)
    }
  }, [selectedDate, allTimeSlots])

  if (!selectedDate) {
    return (
      <div className="time_slot_picker">
        <p className="time_slot_picker_message">Please select a date to view available time slots</p>
      </div>
    )
  }

  const contentClassName = isFramed() ? 'time_slot_picker_content match-screen-app--framed' : 'time_slot_picker_content'

  return (
    <div className="time_slot_picker">
      <div className="time_slot_picker_header">
        <h3>Available Times ({userTimezone})</h3>
      </div>
      <div className={contentClassName}>
        <TimeSlotSection title="Morning" slots={filteredSlots.morning} selectedTime={selectedTime} onSelectTime={onSelectTime} />
        <TimeSlotSection title="Afternoon" slots={filteredSlots.afternoon} selectedTime={selectedTime} onSelectTime={onSelectTime} />
        <TimeSlotSection title="Late Afternoon" slots={filteredSlots.lateAfternoon} selectedTime={selectedTime} onSelectTime={onSelectTime} />
      </div>
    </div>
  )
}

export default TimeSlotPicker
